import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Pencil, FileText, Settings, CheckCircle, User, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function HomePage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold mb-4">Welcome to AISEOBlogGenerator Documentation</h1>
        <p className="text-lg text-gray-700 mb-6">
          This documentation will help you learn how to use AISEOBlogGenerator to create high-quality, 
          SEO-optimized blog content that doesn't suck.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3 text-blue-800">Quick Start Guide</h2>
        <ol className="space-y-4 list-decimal ml-6">
          <li className="text-gray-700">
            <span className="font-medium">Create an account or log in</span> to access the content generation tools.
          </li>
          <li className="text-gray-700">
            <span className="font-medium">Navigate to the Content Writer</span> from the top navigation bar or dashboard.
          </li>
          <li className="text-gray-700">
            <span className="font-medium">Fill in the required fields</span> (title, keywords, etc.) to generate your first blog post.
          </li>
          <li className="text-gray-700">
            <span className="font-medium">Customize the output</span> using the provided editing tools.
          </li>
          <li className="text-gray-700">
            <span className="font-medium">Save or export your content</span> to your preferred platform.
          </li>
        </ol>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <CardLink
          title="Content Writer"
          description="Learn how to use the AI-powered content writer to create blog posts."
          icon={<Pencil className="w-5 h-5" />}
          href="/docs/writer"
        />
        <CardLink
          title="Managing Posts"
          description="Organize, edit, and export your generated content."
          icon={<FileText className="w-5 h-5" />}
          href="/docs/posts"
        />
        <CardLink
          title="Strike List"
          description="Customize your content by defining words and phrases to avoid."
          icon={<CheckCircle className="w-5 h-5" />}
          href="/docs/strike-list"
        />
        <CardLink
          title="Account Settings"
          description="Manage your account preferences and settings."
          icon={<Settings className="w-5 h-5" />}
          href="/docs/settings"
        />
        <CardLink
          title="Subscription Plans"
          description="Learn about our different subscription tiers and features."
          icon={<User className="w-5 h-5" />}
          href="/docs/plans"
        />
      </div>

      <div className="mt-12 bg-gray-50 border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3">Need Help?</h2>
        <p className="text-gray-700 mb-4">
          If you can't find what you're looking for in our documentation, our support team is here to help.
        </p>
        <Button asChild variant="outline">
          <Link to="/contact" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Contact Support
          </Link>
        </Button>
      </div>
    </div>
  );
}

interface CardLinkProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
}

function CardLink({ title, description, icon, href }: CardLinkProps) {
  return (
    <Link 
      to={href} 
      className="block group rounded-lg border bg-white p-6 shadow-sm transition-all hover:border-blue-200 hover:shadow"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="bg-blue-100 text-blue-700 p-2 rounded-lg">
          {icon}
        </div>
        <h3 className="font-semibold text-lg">{title}</h3>
      </div>
      <p className="text-gray-600 mb-3">{description}</p>
      <div className="flex items-center text-blue-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
        Learn more <ArrowRight className="w-4 h-4 ml-1" />
      </div>
    </Link>
  );
}